import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ClockIcon,
  CalendarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';

import './Dashboard.css';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { useAuth } from '../contexts/AuthContext';
import { usePerformanceBarContext } from '../contexts/PerformanceBarContext';
import DashboardService from '../services/dashboard.service';
import AttendanceService from '../services/attendance.service';
import toast from '../utils/toast';
import { formatDateToTime12h, formatTime12hFromDb } from '../utils/timeDisplay';
import { formatDateDisplay } from '../utils/dateDisplay';
import { useToastContext } from '../contexts/ToastContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showPerformanceBar } = usePerformanceBarContext();
  const toastContext = useToastContext();
  const [dashboardData, setDashboardData] = useState({
    attendanceRate: 0,
    totalLeaves: 0,
    pendingRequests: 0,
    workHours: 0,
    weeklyData: [0, 0, 0, 0, 0, 0, 0],
    monthlyAttendance: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    weeklyWorkHours: [0, 0, 0, 0, 0, 0, 0],
    monthlyTrend: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    attendanceBreakdown: { present: 85, absent: 10, late: 5 },
    recentActivity: [],
    todayStatus: { marked: false }
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch dashboard stats from enhanced endpoint
      const dashboardStats = await DashboardService.getStats();
      
      console.log('Dashboard stats received:', dashboardStats);

      setDashboardData({
        attendanceRate: dashboardStats.attendanceRate || 0,
        totalLeaves: dashboardStats.totalLeaves ?? 0,
        pendingRequests: dashboardStats.pendingRequests || 0,
        workHours: dashboardStats.workHours || 0,
        weeklyData: dashboardStats.weeklyWorkHours || [0, 0, 0, 0, 0, 0, 0],
        weeklyWorkHours: dashboardStats.weeklyWorkHours || [0, 0, 0, 0, 0, 0, 0],
        monthlyAttendance: dashboardStats.monthlyTrend || Array(12).fill(dashboardStats.attendanceRate || 0),
        monthlyTrend: dashboardStats.monthlyTrend || Array(12).fill(dashboardStats.attendanceRate || 0),
        attendanceBreakdown: dashboardStats.attendanceBreakdown || { present: 85, absent: 10, late: 5 },
        recentActivity: dashboardStats.recentActivity || [],
        todayStatus: dashboardStats.todayStatus || { marked: false },
        attendanceData: dashboardStats.attendanceData || []
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      const errorMessage = error.message || error.response?.data?.message || error.response?.data?.error || 'Failed to load dashboard data';
      toast.error(errorMessage);
      // Keep default values on error for graceful degradation
    } finally {
      setLoading(false);
    }
  };



  const handleQuickAction = async (actionType) => {
    console.log('🎯 QUICK ACTION CALLED:', actionType); // Debug log
    setActionLoading(actionType);
    
    try {
      switch (actionType) {
        case 'markAttendance':
          await handleMarkAttendance();
          break;
        case 'requestLeave':
          navigate('/leave-request');
          break;
        case 'viewReports':
          // Direct large toast test
          console.log('📊 DIRECT LARGE TOAST TEST');
          if (toastContext) {
            toastContext.info(
              <div>
                <h4>📊 Quick Analytics Overview</h4>
                <div className="data-summary">
                  {JSON.stringify({
                    attendance: {
                      today: `${dashboardData.attendanceRate}%`,
                      thisWeek: '92.3%',
                      thisMonth: '89.7%'
                    },
                    leaves: {
                      pending: dashboardData.pendingRequests || 0,
                      approved: dashboardData.totalLeaves || 0,
                      remaining: 'Varies by employee'
                    },
                    productivity: {
                      avgHours: '8.2 hrs/day',
                      efficiency: '94.5%',
                      projects: 'On track'
                    }
                  }, null, 2)}
                </div>
              </div>,
              'large',
              6000
            );
          } else {
            console.error('Toast context not available');
          }
          
          // Navigate after showing analytics
          setTimeout(() => navigate('/reports'), 3000);
          break;
        case 'showPerformance':
          // Show performance bar with personal metrics
          const performanceData = {
            employees: '1',
            attendance: `${dashboardData.attendanceRate || 0}%`,
            attendanceToday: `${dashboardData.attendanceRate || 0}%`,
            attendanceWeek: dashboardData.weeklyAttendanceRate ? `${dashboardData.weeklyAttendanceRate}%` : 'N/A',
            attendanceMonth: dashboardData.monthlyAttendanceRate ? `${dashboardData.monthlyAttendanceRate}%` : 'N/A',
            pending: dashboardData.pendingRequests || 0,
            leaves: dashboardData.totalLeaves || 0,
            remainingLeaves: dashboardData.remainingLeaves || 0,
            projects: dashboardData.activeProjects || 0,
            completedProjects: dashboardData.completedProjects || 0,
            onTrackProjects: 'N/A',
            productivity: 'N/A',
            efficiency: 'N/A',
            quality: 'N/A',
            growth: 'N/A',
            avgHours: 'N/A',
            innovation: 'N/A',
            goalsMet: 'N/A'
          };
          
          showPerformanceBar(performanceData);
          break;
        case 'teamCalendar':
          navigate('/calendar');
          break;
        default:
          toast.error('Action not implemented');
      }
    } catch (error) {
      console.error('Quick action error:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to perform action';
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkAttendance = async () => {
    try {
      // Check if already marked today
      if (dashboardData.todayStatus.marked) {
        toast.info(
          `Attendance already marked at ${
            dashboardData.todayStatus.inTime ? formatTime12hFromDb(dashboardData.todayStatus.inTime) : 'N/A'
          }`
        );
        return;
      }

      const now = new Date();
      const pad2 = (n) => String(n).padStart(2, '0');
      const currentTime = `${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}`;

      await AttendanceService.markAttendance({
        intime: currentTime,
        status: 'Present'
      });

      toast.success(`Attendance marked successfully at ${formatDateToTime12h(now)}!`);
      
      // Refresh dashboard data to show updated status
      await fetchDashboardData();
    } catch (error) {
      console.error('Error marking attendance:', error);
      const errorMessage = error.message || error.response?.data?.message || error.response?.data?.error || 'Failed to mark attendance';
      toast.error(errorMessage);
      throw error;
    }
  };

  const stats = [
    {
      name: 'Attendance Rate',
      value: `${dashboardData.attendanceRate}%`,
      icon: CheckCircleIcon,
      theme: 'attendance',
      change: dashboardData.todayStatus.marked ? 'Present today' : 'Not marked'
    },
    {
      name: 'Leave Balance',
      value: `${dashboardData.totalLeaves} Days`,
      icon: CalendarIcon,
      theme: 'leave',
      change: `${dashboardData.totalLeaves} remaining`
    },
    {
      name: 'Pending Requests',
      value: dashboardData.pendingRequests,
      icon: ExclamationTriangleIcon,
      theme: 'pending',
      change: dashboardData.pendingRequests > 0 ? `${dashboardData.pendingRequests} pending` : 'All clear'
    },
    {
      name: 'Work Hours (Month)',
      value: `${dashboardData.workHours}h`,
      icon: ClockIcon,
      theme: 'hours',
      change: dashboardData.todayStatus.workHours ? `Today: ${dashboardData.todayStatus.workHours}` : 'No data today'
    }
  ];

  const weeklyChartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Work Hours',
        data: dashboardData.weeklyWorkHours,
        backgroundColor: '#3B82F6',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      }
    ]
  };

  const attendanceChartData = {
    labels: ['Present', 'Absent', 'Late'],
    datasets: [
      {
        data: [
          dashboardData.attendanceBreakdown.present,
          dashboardData.attendanceBreakdown.absent,
          dashboardData.attendanceBreakdown.late
        ],
        backgroundColor: [
          '#10B981',
          '#EF4444',
          '#F59E0B'
        ],
        borderWidth: 0,
      }
    ]
  };

  const monthlyTrendData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Attendance %',
        data: dashboardData.monthlyTrend,
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { color: '#64748B', usePointStyle: true, boxWidth: 8, padding: 20 },
      },
    },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { color: '#64748B' } },
      y: {
        beginAtZero: true,
        grid: { color: '#F1F5F9' },
        border: { display: false },
        ticks: { color: '#64748B' },
      },
    },
  };

  const quickActions = [
    { 
      name: dashboardData.todayStatus.marked ? 'Attendance Marked' : 'Mark Attendance', 
      icon: CheckCircleIcon, 
      color: dashboardData.todayStatus.marked ? 'bg-emerald-100' : 'bg-teal-100',
      iconColor: dashboardData.todayStatus.marked ? 'text-emerald-700' : 'text-teal-700',
      buttonColor: dashboardData.todayStatus.marked
        ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 focus:ring-emerald-400'
        : 'border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100 focus:ring-teal-400',
      action: 'markAttendance',
      disabled: dashboardData.todayStatus.marked
    },
    { 
      name: 'Request Leave', 
      icon: CalendarIcon, 
      color: 'bg-sky-100',
      iconColor: 'text-sky-700',
      buttonColor: 'border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 focus:ring-sky-400',
      action: 'requestLeave'
    },
    { 
      name: 'Performance', 
      icon: ChartBarIcon, 
      color: 'bg-violet-100',
      iconColor: 'text-violet-700',
      buttonColor: 'border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100 focus:ring-violet-400',
      action: 'showPerformance'
    },
    { 
      name: 'Team Calendar', 
      icon: UserGroupIcon, 
      color: 'bg-amber-100',
      iconColor: 'text-amber-700',
      buttonColor: 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 focus:ring-amber-400',
      action: 'teamCalendar'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hrms-dashboard">
      {/* Welcome Section */}
      <motion.div
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        className="dashboard-welcome"
      >
        <div className="dashboard-welcome-content">
          <div className="dashboard-welcome-copy">
            <h1 className="dashboard-greeting">
              Welcome back, {user?.first_name}!
            </h1>
            <p className="dashboard-welcome-subtitle">
              Here's what's happening with your work today.
            </p>
            {dashboardData.todayStatus.marked && (
              <div className="dashboard-attendance-status">
                <span className="dashboard-attendance-dot" />
                <span>
                  Attendance marked at{' '}
                  {dashboardData.todayStatus.inTime ? formatTime12hFromDb(dashboardData.todayStatus.inTime) : 'N/A'}
                </span>
              </div>
            )}
            <p className="dashboard-welcome-date">
              {formatDateDisplay(new Date())}
            </p>
          </div>

          <div className="dashboard-welcome-visual" aria-hidden="true">
            <img
              src={`${process.env.PUBLIC_URL}/assets/welcome-mountain.png`}
              alt=""
              className="dashboard-welcome-image"
            />
            <p className="dashboard-motivation">
              <span className="dashboard-motivation-line dashboard-motivation-first">Have</span>
              <span className="dashboard-motivation-line dashboard-motivation-second">A Great</span>
              <span className="dashboard-motivation-line dashboard-motivation-third">Day !</span>
            </p>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="dashboard-stats">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card initial={false} className={`dashboard-panel bg-white dashboard-stat dashboard-theme-${stat.theme}`}>
              <div className="flex items-start gap-4">
                <div className="dashboard-stat-icon">
                  <stat.icon className="w-6 h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="dashboard-stat-label">{stat.name}</p>
                  <p className="dashboard-stat-value">{stat.value}</p>
                  <p className="dashboard-stat-note">
                    {stat.change}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <Card initial={false} className="dashboard-panel bg-white">
        <Card.Header className="dashboard-panel-header bg-white">
          <div className="dashboard-actions-heading flex items-center justify-between">
            <div>
              <h3 className="dashboard-heading">Quick Actions</h3>
              <p className="dashboard-subtitle">Frequently used actions</p>
            </div>
            <Button
              variant="softBlue"
              whileHover={{}}
              whileTap={{}}
              size="sm"
              onClick={fetchDashboardData}
              disabled={loading}
              className="flex items-center space-x-2"
            >
              <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh</span>
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="dashboard-panel-body">
          <div className="dashboard-actions">
            {quickActions.map((action, index) => (
              <motion.div
                key={action.name}
                initial={false}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <Button
                  variant="outline"
                  whileHover={{}}
                  whileTap={{}}
                  className={`dashboard-action dashboard-action-${action.action} flex-col space-y-2 w-full ${action.buttonColor}`}
                  onClick={() => handleQuickAction(action.action)}
                  loading={actionLoading === action.action}
                  disabled={actionLoading !== null || action.disabled}
                >
                  <div className={`p-2 rounded-lg ${action.color}`}>
                    <action.icon className={`w-5 h-5 ${action.iconColor}`} />
                  </div>
                  <span className="text-xs font-medium">{action.name}</span>
                </Button>
              </motion.div>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Charts Section */}
      <div className="dashboard-charts grid lg:grid-cols-3 gap-6">
        {/* Weekly Work Hours */}
        <Card initial={false} className="dashboard-panel bg-white lg:col-span-2">
          <Card.Header className="dashboard-panel-header bg-white">
            <h3 className="dashboard-heading">Weekly Work Hours</h3>
            <p className="dashboard-subtitle">Your work hours for this week</p>
          </Card.Header>
          <Card.Body className="dashboard-panel-body">
            <div className="dashboard-chart">
              <Bar data={weeklyChartData} options={chartOptions} />
            </div>
          </Card.Body>
        </Card>

        {/* Attendance Breakdown */}
        <Card initial={false} className="dashboard-panel bg-white">
          <Card.Header className="dashboard-panel-header bg-white">
            <h3 className="dashboard-heading">Attendance</h3>
            <p className="dashboard-subtitle">This month's breakdown</p>
          </Card.Header>
          <Card.Body className="dashboard-panel-body">
            <div className="dashboard-chart">
              <Doughnut 
                data={attendanceChartData} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: { color: '#64748B', usePointStyle: true, pointStyle: 'circle', boxWidth: 8, padding: 20 },
                    },
                  },
                }}
              />
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Monthly Trend */}
      <Card initial={false} className="dashboard-panel bg-white">
        <Card.Header className="dashboard-panel-header bg-white">
          <h3 className="dashboard-heading">Attendance Trend</h3>
          <p className="dashboard-subtitle">Your attendance percentage over the year</p>
        </Card.Header>
        <Card.Body className="dashboard-panel-body">
          <div className="dashboard-chart">
            <Line data={monthlyTrendData} options={chartOptions} />
          </div>
        </Card.Body>
      </Card>

      {/* Recent Activity */}
      <Card initial={false} className="dashboard-panel bg-white">
        <Card.Header className="dashboard-panel-header bg-white">
          <h3 className="dashboard-heading">Recent Activity</h3>
          <p className="dashboard-subtitle">Your latest actions and updates</p>
        </Card.Header>
        <Card.Body className="dashboard-panel-body">
          <div className="space-y-4">
            {dashboardData.recentActivity.length > 0 ? (
              dashboardData.recentActivity.map((activity, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="dashboard-activity flex flex-wrap gap-3 items-center justify-between py-3 border-b border-slate-100 last:border-b-0"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full ${
                      activity.status === 'success' ? 'bg-success-500' :
                      activity.status === 'pending' ? 'bg-warning-500' :
                      activity.status === 'rejected' ? 'bg-danger-500' :
                      'bg-primary-500'
                    }`} />
                    <span className="text-sm text-gray-900">{activity.action}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">{activity.time}</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      activity.status === 'success' ? 'bg-success-100 text-success-800' :
                      activity.status === 'pending' ? 'bg-warning-100 text-warning-800' :
                      activity.status === 'rejected' ? 'bg-danger-100 text-danger-800' :
                      'bg-primary-100 text-primary-800'
                    }`}>
                      {activity.status}
                    </span>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ClockIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No recent activity</p>
                <p className="text-sm">Your activities will appear here</p>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Dashboard;
